// Touch controls: virtual stick, buttons, and throttle for coarse-pointer
// (phone/tablet) play.
//
// Layout, tuned for two thumbs in landscape:
//   - Left half of the screen is a dynamic virtual stick: touch anywhere and
//     the stick anchors under the thumb. Deflection drives the same aim
//     cursor mouse players steer with, so the flight-assist controller does
//     the actual flying; release and the cursor eases back to center for
//     straight flight. Fly it hands-off: let go of the stick and, once the
//     wings settle, the autopilot latches and holds straight & level (main.js
//     owns the latch); grab the stick again and you have it. While manning an
//     AA gun the cursor maps to turret slew the same way it does for mouse aim.
//   - Right half is a freelook drag area (the touch equivalent of holding
//     the right mouse button), with FIRE and BOMB riding on top in the
//     bottom corner. BAIL replaces the flight buttons while gunning.
//   - A throttle slider hugs the left edge; its position is absolute, like
//     a HOTAS lever. In the space theater it becomes a DEAD-MAN lever
//     instead: four labelled areas (¼ / ½ / ¾ / MAX), live only while a
//     thumb holds one, springing shut on release — a bell left open rewrites
//     your orbit, so out there "set and forget" is a way to lose a round.
//   - Tapping the top score bar toggles the scoreboard.

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/** True when the primary input is a touch screen (not a touch laptop). */
export function isTouchDevice() {
  return (navigator.maxTouchPoints ?? 0) > 0 && matchMedia("(pointer: coarse)").matches;
}

const STICK_RADIUS = 72; // knob travel in px (more travel = finer quantization)
const CURSOR_RANGE = 0.55; // matches the aim-cursor clamp in input.js
const STICK_EXPO = 2.0; // response curve: finer control near center
const STICK_DEAD = 0.06; // ignore resting-thumb jitter near center
const LOOK_SENS = 0.007; // rad per px of freelook drag

export class TouchControls {
  constructor(input) {
    this.input = input;
    input.touch = this;

    this.fire = false;
    this.bomb = false;
    this.msl = false; // jets era: heat-seeker launch held
    this.flare = false; // jets era: flare dispenser held
    this.throttleValue = input.throttle;
    this.stick = { id: null, x: 0, y: 0, dx: 0, dy: 0 };
    // Stick axes for the flight instructor (steerStickAssist): post-deadzone,
    // linear, +ax right / +ay up. The aim cursor below still feeds gun and
    // turret slew — flying is stick semantics, aiming is cursor semantics.
    this.ax = 0;
    this.ay = 0;
    this.lookId = null;
    this.lookX = 0;
    this.lookY = 0;
    this.thrId = null;
    // The space theater's lever: spring-loaded (fires only while held) and
    // divided into quarter areas. setThrottleMode() flips both on.
    this.thrSpring = false;
    this.thrZoned = false;
    this.visible = false;

    const root = document.createElement("div");
    root.id = "touch-controls";
    root.classList.add("hidden");
    root.innerHTML = `
      <div id="tc-steer-zone" class="tc-zone"></div>
      <div id="tc-look-zone" class="tc-zone"></div>
      <div id="tc-stick-base"><div id="tc-stick-knob"></div></div>
      <div id="tc-throttle"><div id="tc-throttle-fill"></div><div id="tc-throttle-zones" class="hidden"></div><span>THR 0</span></div>
      <button id="tc-zoom" class="tc-btn hidden">ZOOM</button>
      <button id="tc-msl" class="tc-btn hidden">MSL</button>
      <button id="tc-flare" class="tc-btn hidden">FLARE</button>
      <button id="tc-bomb" class="tc-btn">BOMB</button>
      <button id="tc-fire" class="tc-btn">FIRE</button>
      <button id="tc-bail" class="tc-btn">BAIL</button>`;
    document.getElementById("ui").appendChild(root);
    this.root = root;

    // First-flight coach overlay: ghosted labels over the live control zones,
    // each fading the first time its control is used (see maybeShowCoach). Lives
    // inside the flight root so it shows/hides with the controls.
    const coach = document.createElement("div");
    coach.id = "tc-coach";
    coach.classList.add("hidden");
    coach.innerHTML = `
      <div id="coach-steer" class="coach-label"><span class="coach-arc">↻</span><span>STEER</span></div>
      <div id="coach-look" class="coach-label">DRAG TO LOOK</div>
      <div id="coach-thr" class="coach-label">THROTTLE</div>`;
    root.appendChild(coach);
    this.coach = coach;
    this.coachSteer = coach.querySelector("#coach-steer");
    this.coachLook = coach.querySelector("#coach-look");
    this.coachThr = coach.querySelector("#coach-thr");
    this.coachCleared = new Set();

    const $ = (id) => root.querySelector(`#${id}`);
    this.base = $("tc-stick-base");
    this.knob = $("tc-stick-knob");
    this.thrFill = $("tc-throttle-fill");
    this.thrZones = $("tc-throttle-zones");
    this.thrLabel = $("tc-throttle").querySelector("span");
    this.bombBtn = $("tc-bomb");
    this.zoomBtn = $("tc-zoom");
    this.setThrottleValue(this.throttleValue);

    root.addEventListener("contextmenu", (e) => e.preventDefault());
    // Block iOS pinch/double-tap zoom from breaking the playfield.
    document.addEventListener("gesturestart", (e) => e.preventDefault());
    document.addEventListener("dblclick", (e) => e.preventDefault());

    // --- virtual stick (left half, anchors where the thumb lands) ---
    const steer = $("tc-steer-zone");
    steer.addEventListener("pointerdown", (e) => {
      if (this.stick.id !== null) return;
      e.preventDefault();
      this.clearCoachLabel("steer");
      // A thumb on the stick means you're flying it: main.js drops the hands-off
      // autopilot the moment it sees the stick held, so grabbing it hands you
      // back control instantly.
      const s = this.stick;
      try {
        steer.setPointerCapture(e.pointerId);
      } catch {
        /* pointer already lifted (very fast tap) — tracking still works */
      }
      s.id = e.pointerId;
      // Keep the base circle fully on screen even for edge touches.
      // The lower bound clears the throttle rail's column (see the CSS
      // note on #tc-steer-zone): the base circle must never DRAW over the
      // lever either, or the old under-the-thumb bug looks alive.
      s.x = clamp(e.clientX, STICK_RADIUS + 104, innerWidth / 2);
      s.y = clamp(e.clientY, STICK_RADIUS + 64, innerHeight - STICK_RADIUS - 8);
      s.dx = s.dy = 0;
      this.base.style.left = `${s.x}px`;
      this.base.style.top = `${s.y}px`;
      this.base.classList.add("on");
      this.moveKnob();
    });
    steer.addEventListener("pointermove", (e) => {
      const s = this.stick;
      if (e.pointerId !== s.id) return;
      let dx = (e.clientX - s.x) / STICK_RADIUS;
      let dy = (e.clientY - s.y) / STICK_RADIUS;
      const m = Math.hypot(dx, dy);
      if (m > 1) {
        dx /= m;
        dy /= m;
      }
      s.dx = dx;
      s.dy = dy;
      this.moveKnob();
    });
    const steerEnd = (e) => {
      if (e.pointerId !== this.stick.id) return;
      // Releasing the stick starts the hands-off clock (main.js latches the
      // autopilot once the wings settle); grabbing it again drops it.
      this.stick.id = null;
      this.base.classList.remove("on");
    };
    steer.addEventListener("pointerup", steerEnd);
    steer.addEventListener("pointercancel", steerEnd);

    // --- freelook drag (right half, under the buttons) ---
    const look = $("tc-look-zone");
    look.addEventListener("pointerdown", (e) => {
      if (this.lookId !== null) return;
      e.preventDefault();
      this.clearCoachLabel("look");
      look.setPointerCapture(e.pointerId);
      this.lookId = e.pointerId;
      this.lookX = e.clientX;
      this.lookY = e.clientY;
      this.input.look.active = true;
    });
    look.addEventListener("pointermove", (e) => {
      if (e.pointerId !== this.lookId) return;
      const lk = this.input.look;
      lk.yaw = clamp(lk.yaw + (e.clientX - this.lookX) * LOOK_SENS, -Math.PI, Math.PI);
      lk.pitch = clamp(lk.pitch + (e.clientY - this.lookY) * LOOK_SENS, -1.2, 1.35);
      this.lookX = e.clientX;
      this.lookY = e.clientY;
    });
    const lookEnd = (e) => {
      if (e.pointerId !== this.lookId) return;
      this.lookId = null;
      this.input.look.active = false;
    };
    look.addEventListener("pointerup", lookEnd);
    look.addEventListener("pointercancel", lookEnd);

    // --- throttle slider ---
    // Two levers live in this rail. A WING's throttle is absolute and
    // latching: you set cruise and fly, because an aeroplane that loses
    // power falls out of the sky. A BELL's is a dead-man lever divided into
    // four labelled areas: put a thumb in ¼ / ½ / ¾ / MAX and it burns at
    // that setting for exactly as long as you hold it, then springs shut.
    // Out there a forgotten lever is not cruise, it's an escape trajectory —
    // and thumbing a zone is something you can do without looking, which
    // sliding a continuous lever never was.
    const thr = $("tc-throttle");
    const setThrottle = (e) => {
      const r = thr.getBoundingClientRect();
      let v = clamp(1 - (e.clientY - r.top) / r.height, 0, 1);
      if (this.thrZoned) {
        // Snap to the area under the thumb: four equal bands, ¼ at the
        // bottom through MAX at the top. There is no "closed" band because
        // closed is what LETTING GO means — a thumb on the rail is always
        // asking for power, and that is the whole grammar of the control.
        const z = Math.ceil(v * 4);
        v = z <= 0 ? 0.25 : z / 4;
      } else if (v > 0.95) v = 1; // the two settings that matter in a knife fight
      else if (v < 0.05) v = 0;
      this.setThrottleValue(v);
    };
    thr.addEventListener("pointerdown", (e) => {
      if (this.thrId !== null) return;
      e.preventDefault();
      this.clearCoachLabel("thr");
      thr.setPointerCapture(e.pointerId);
      this.thrId = e.pointerId;
      setThrottle(e);
    });
    thr.addEventListener("pointermove", (e) => {
      if (e.pointerId === this.thrId) setThrottle(e);
    });
    const thrEnd = (e) => {
      if (e.pointerId !== this.thrId) return;
      this.thrId = null;
      // The spring: let go and the bell goes quiet. (A wing's lever stays
      // where the thumb left it.)
      if (this.thrSpring) this.setThrottleValue(0);
    };
    thr.addEventListener("pointerup", thrEnd);
    thr.addEventListener("pointercancel", thrEnd);

    // --- buttons ---
    const bindHold = (el, set) => {
      el.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        el.setPointerCapture(e.pointerId);
        set(true);
        el.classList.add("held");
      });
      const off = () => {
        set(false);
        el.classList.remove("held");
      };
      el.addEventListener("pointerup", off);
      el.addEventListener("pointercancel", off);
    };
    bindHold($("tc-fire"), (v) => (this.fire = v));
    bindHold(this.bombBtn, (v) => (this.bomb = v));
    // Jets era: MSL and FLARE (hidden until main.js shows them for a plane
    // that carries the kit — the server edge-detects, so holds are safe).
    this.mslBtn = $("tc-msl");
    this.flareBtn = $("tc-flare");
    bindHold(this.mslBtn, (v) => (this.msl = v));
    bindHold(this.flareBtn, (v) => (this.flare = v));
    $("tc-bail").addEventListener("pointerdown", (e) => {
      e.preventDefault();
      this.input.bailRequested?.();
    });
    // Naval gunsight toggle (the Z key's touch equivalent, shown at sea).
    this.zoomBtn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      this.input.zoomToggle?.();
    });

    // Tap the score bar to peek at the scoreboard (TAB equivalent).
    document.getElementById("top-bar").addEventListener("pointerdown", (e) => {
      e.preventDefault();
      this.input.scoreboard = !this.input.scoreboard;
    });

    // --- spectate bar (lives outside the flight root so it can show while
    // the stick/buttons are hidden). PREV/NEXT are the touch equivalent of
    // the N / P keys; tapping the open playfield also advances forward.
    // RECENTER (C key) snaps the orbit camera back behind the followed plane —
    // swipe the field to rotate the camera around it.
    const spec = document.createElement("div");
    spec.id = "tc-spectate";
    spec.classList.add("hidden");
    spec.innerHTML = `
      <button id="tc-spec-prev" class="tc-btn">‹ PREV</button>
      <button id="tc-spec-recenter" class="tc-btn">RECENTER</button>
      <button id="tc-spec-next" class="tc-btn">NEXT ›</button>`;
    document.getElementById("ui").appendChild(spec);
    this.specBar = spec;
    const cycle = (dir) => (e) => {
      e.preventDefault();
      this.input.spectateCycle?.(dir);
    };
    spec.querySelector("#tc-spec-prev").addEventListener("pointerdown", cycle(-1));
    spec.querySelector("#tc-spec-next").addEventListener("pointerdown", cycle(1));
    spec.querySelector("#tc-spec-recenter").addEventListener("pointerdown", (e) => {
      e.preventDefault();
      this.input.spectateRecenter?.();
    });
  }

  /** Show the PREV/NEXT vantage controls (only while spectating). */
  setSpectate(v) {
    this.specBar.classList.toggle("hidden", !v);
  }

  moveKnob() {
    const s = this.stick;
    this.knob.style.transform = `translate(${s.dx * STICK_RADIUS}px, ${s.dy * STICK_RADIUS}px)`;
  }

  /** Called every frame before input.update: stick deflection → aim cursor. */
  update(dt) {
    const c = this.input.cursor;
    const s = this.stick;
    if (s.id !== null) {
      const m0 = Math.hypot(s.dx, s.dy);
      // Deadzone then rescale: a resting thumb (m0 < STICK_DEAD) commands
      // nothing, and the remaining travel still reaches full deflection.
      const m = m0 < STICK_DEAD ? 0 : (m0 - STICK_DEAD) / (1 - STICK_DEAD);
      const r = m0 > 0 ? (Math.pow(m, STICK_EXPO) * CURSOR_RANGE) / m0 : 0;
      c.x = s.dx * r;
      c.y = -s.dy * r;
      // Flight axes: linear past the deadzone (the instructor applies its own
      // curve — see STICK_BANK_EXPO in fdm.js).
      const lin = m0 > 0 ? m / m0 : 0;
      this.ax = s.dx * lin;
      this.ay = -s.dy * lin;
    } else {
      // Hands off: ease back to straight-and-level. The flight axes drop to
      // zero at once — the stick instructor's own loops fly the roll-out.
      const k = 1 - Math.exp(-7 * dt);
      c.x += (0 - c.x) * k;
      c.y += (0.04 - c.y) * k;
      this.ax = 0;
      this.ay = 0;
    }
  }

  /** Paint the lever: the fill, the percent, and the held-zone highlight. */
  setThrottleValue(v) {
    this.throttleValue = v;
    this.thrFill.style.height = `${v * 100}%`;
    this.thrLabel.textContent = `THR ${Math.round(v * 100)}`;
    if (this.thrZoned && this.thrZones) {
      const live = Math.ceil(v * 4);
      for (const el of this.thrZones.children) {
        el.classList.toggle("live", Number(el.dataset.zone) <= live);
      }
    }
  }

  /**
   * Pick which lever this theater flies. `spring` makes it a dead-man
   * control — it commands only while a thumb is on it and springs closed on
   * release; `zoned` divides the rail into four labelled areas so a thumb
   * can find a setting without watching it. The space theater takes both;
   * everyone else keeps the absolute HOTAS lever they always had.
   */
  setThrottleMode({ spring = false, zoned = false } = {}) {
    this.thrSpring = spring;
    this.thrZoned = zoned;
    this.thrZones.classList.toggle("hidden", !zoned);
    if (zoned && !this.thrZones.children.length) {
      // Bottom to top: the closed detent is unlabelled (it's the resting
      // state), then the three working settings and the wall.
      for (const [z, label] of [[4, "MAX"], [3, "¾"], [2, "½"], [1, "¼"]]) {
        const el = document.createElement("div");
        el.className = "tc-thr-zone";
        el.dataset.zone = String(z);
        el.textContent = label;
        this.thrZones.appendChild(el);
      }
    }
    if (spring) this.setThrottleValue(0);
  }

  /** Show the control layer only while flying or gunning. */
  setVisible(v) {
    if (v === this.visible) return;
    this.visible = v;
    this.root.classList.toggle("hidden", !v);
    if (!v) {
      this.fire = false;
      this.bomb = false;
      this.stick.id = null;
      this.thrId = null;
      if (this.thrSpring) this.setThrottleValue(0); // the layer went away with a thumb on the lever
      this.base.classList.remove("on");
      if (this.lookId !== null) {
        this.lookId = null;
        this.input.look.active = false;
      }
    }
  }

  /** Gun mode swaps the flight cluster (throttle/bomb) for a BAIL button;
   * drive mode (a vehicle or warship) keeps the throttle — a ship needs its
   * engine telegraph — and adds BAIL (F, leave the ship). */
  setMode(manning, driving = false) {
    this.root.classList.toggle("gun-mode", manning);
    this.root.classList.toggle("drive-mode", !manning && driving);
  }

  setBombVisible(v) {
    this.bombBtn.classList.toggle("hidden", !v);
  }

  /** Jets era: show MSL / FLARE for a plane that carries the kit. */
  setJetsVisible(msl, flare) {
    this.mslBtn.classList.toggle("hidden", !msl);
    this.flareBtn.classList.toggle("hidden", !flare);
    if (!msl) this.msl = false;
    if (!flare) this.flare = false;
  }

  /** Show the naval ZOOM (gunsight) button while conning a warship. */
  setZoomVisible(v) {
    this.zoomBtn.classList.toggle("hidden", !v);
  }

  /** Show the first-flight control coach once (guarded by localStorage). Called
   *  by main.js when a touch player first spawns into a plane. */
  maybeShowCoach() {
    if (this._coachShown) return;
    try {
      if (localStorage.getItem("va-touch-coach")) return;
    } catch {
      /* private mode / disabled storage: just show it this session */
    }
    this._coachShown = true;
    this.coachCleared.clear();
    this.coachSteer.classList.remove("faded");
    this.coachLook.classList.remove("faded");
    this.coachThr.classList.remove("faded");
    this.coach.classList.remove("hidden");
    clearTimeout(this._coachTimer);
    this._coachTimer = setTimeout(() => this.dismissCoach(), 20000);
  }

  /** Fade one label the first time its control is used; dismiss once all clear. */
  clearCoachLabel(which) {
    if (!this._coachShown || this.coachCleared.has(which)) return;
    const el =
      which === "steer" ? this.coachSteer : which === "look" ? this.coachLook : this.coachThr;
    el?.classList.add("faded");
    this.coachCleared.add(which);
    if (this.coachCleared.size >= 3) this.dismissCoach();
  }

  dismissCoach() {
    clearTimeout(this._coachTimer);
    this.coach.classList.add("hidden");
    this._coachShown = false;
    try {
      localStorage.setItem("va-touch-coach", "1");
    } catch {
      /* ignore */
    }
  }
}
